import {useEffect, useState} from "react";
import {DataViewerPaginater, FilterObject, QueryObject} from "./types";
import {objectsEqualNew} from "./util";
import {collection, DocumentData, documentId, endAt, endBefore, getDocs, limit, limitToLast, onSnapshot, orderBy, query, QueryDocumentSnapshot, QueryFieldFilterConstraint, QuerySnapshot, startAfter, where} from "firebase/firestore";
import {db} from "./firebaseConfig";
import {Unsubscribe} from "firebase/auth";

export function useFilterTablePaginator({data}:{data:{[key:string]:{[key:string]: unknown}}|QueryObject[]}) {
    const [tableData, setTableData] = useState<{[key:string]:{[key:string]: unknown}}>(Array.isArray(data) ? Object.fromEntries(Object.entries(data).slice(0, 10)) : {});
    const [page, setPage] = useState([1, 0]);
    const [filters, setFilters] = useState<{[key:string]: unknown}>();
    const [queryAnchor, setQueryAnchor] = useState<{startKey: string, endKey: string, startQueryPos: number, endQueryPos: number}>({startKey: "", endKey: "", startQueryPos: 0, endQueryPos: 0});
    const [prevEntryIds, setPrevEntryIds] = useState<{[key:string]:number}>({});
    const [dataListenerUnsubscribe, setDataListenerUnsubscribe] = useState<Unsubscribe|undefined>();


    const getDataFromQuery = async (
        itemList: {[key: string]: any} = {}, currentQueryAnchor=queryAnchor, cursorDirection?:"increase"|"decrease"|undefined, prevEntries=prevEntryIds, loadMoreFromQuery=false):Promise<any> => {
        if (!Array.isArray(data)) {
            setTableData(Object.fromEntries(Object.entries(data).slice((page[0] - 1)*10, page[0]*10)));
            return;
        }
        let cursorPos:number;
        if (page[0] > page[1]) {
            cursorPos = currentQueryAnchor.endQueryPos;
        } else {
            cursorPos = currentQueryAnchor.startQueryPos;
        }

        const querySchema:QueryObject = data[cursorPos];

        const createQuery = (queryData:QueryObject) => {
            const constraints:any[] = [];
            queryData.where && queryData.where.forEach((w) => {
                constraints.push(where(...w));
            });

            filters && Object.entries(filters).forEach(([key, value]) => {
                constraints.push(where(key, "==", value));
            });

            constraints.push(orderBy(queryData.orderBy ? queryData.orderBy === "documentId" ? documentId() : queryData.orderBy : documentId()));

            if (page[0] > page[1] && !cursorDirection) { // Going up
                currentQueryAnchor.endKey && constraints.push(startAfter(currentQueryAnchor.endKey));
                constraints.push(limit(10));
                if (!loadMoreFromQuery) {
                    currentQueryAnchor = {...currentQueryAnchor, startQueryPos: currentQueryAnchor.endQueryPos};
                }
            } else if (page[0] < page[1] && !cursorDirection) { // Going down
                if (!loadMoreFromQuery) {
                    currentQueryAnchor = {...currentQueryAnchor, endQueryPos: currentQueryAnchor.startQueryPos};
                }
                constraints.push(limitToLast(10));
                if (currentQueryAnchor.startKey) {
                    currentQueryAnchor.startKey && constraints.push(endBefore(currentQueryAnchor.startKey));
                } else {
                    currentQueryAnchor.startKey && constraints.push(endAt(currentQueryAnchor.startKey));
                }
            } else {
                if (cursorDirection === "decrease") {
                    constraints.push(limitToLast(10));
                } else {
                    constraints.push(limit(10));
                }
            }
            return constraints;
        };

        const constraints = createQuery(querySchema);
        // console.log(queryId, "constraints", constraints)

        const q = query(collection(db, ...(querySchema.path as [any])), ...(constraints));
        const queryResults:{[key:string]: unknown} = {};

        const queryData = await getDocs(q);

        // console.log("queryData.size", queryData.size)


        const reverseIfBack = (docs:QueryDocumentSnapshot<DocumentData>[]) => {
            if (page[0] < page[1]) {
                return docs.reverse();
            }
            return docs;
        };

        let index = 0;
        reverseIfBack(queryData.docs).forEach((doc: QueryDocumentSnapshot) => {
            if (Object.keys(queryResults).length + Object.keys(itemList).length === 10) {
                return;
            }

            let position = Object.keys(itemList).length+(page[0]-1)*10+index+1;
            if (page[0] < page[1]) {
                position = (page[0])*10-index-Object.keys(itemList).length;
            }

            // console.log(index, "doc.id", doc.id, position, "E", prevEntries[doc.id])

            if (itemList[doc.id] || (prevEntries[doc.id] && prevEntries[doc.id] !== position)) {
                console.log("Removing ", doc.id, ": E=", prevEntries[doc.id], ", G=", position);
                return;
            }

            const item = doc.data();
            item.id = doc.id;
            queryResults[doc.id] = item;
            index = index+1;

            if (prevEntries[doc.id]) return;
            prevEntries[doc.id] = position;
        });

        if (cursorDirection === "decrease" || page[0] < page[1]) {
            itemList = {...Object.fromEntries(Object.entries(queryResults).reverse()), ...itemList};
        } else {
            itemList = {...itemList, ...queryResults};
        }

        setPrevEntryIds(prevEntries);

        if (queryData.size < 10 && Object.keys(itemList).length < 10) {
            if (page[0] > page[1] && cursorPos+1 < data.length) {
                console.log("Increase query index");
                return getDataFromQuery(itemList, {...currentQueryAnchor, endQueryPos: currentQueryAnchor.endQueryPos+1}, "increase", prevEntries);
            } else if (page[0] < page[1] && cursorPos > 0) {
                console.log("Decrease query index");
                return getDataFromQuery(itemList, {...currentQueryAnchor, startQueryPos: currentQueryAnchor.startQueryPos-1}, "decrease", prevEntries);
            }
        }

        if (Object.keys(itemList).length < 10 && queryData.size === 10) {
            console.log("Shorter than ten");
            // if(loadMoreFromQuery){return}
            return getDataFromQuery(itemList, {...currentQueryAnchor, startKey: Object.keys(itemList)[0], endKey: Object.keys(itemList).slice(-1)[0]}, undefined, prevEntries, true);
        }

        if (queryData.size === 0 &&
            Object.keys(itemList).length === 0 &&
            currentQueryAnchor.endQueryPos+1 === data.length &&
            page[0] > 1) {
            setTableData({});
            setQueryAnchor((a) => ({...a, startKey: ""}));
            return;
        }

        const listenForUpdates = () => {
            if (!Object.keys(itemList).length) return;
            console.log("Fetching filter table updates");
            const itemListUpdateQuery = query(collection(db, ...(querySchema.path as [any])), where(documentId(), "in", Object.keys(itemList)));
            const itemUpdateSnapshot = onSnapshot(itemListUpdateQuery, (querySnapshot) => {
                querySnapshot.docs.forEach((doc) => {
                    setTableData((data) => {
                        const newData = {...data, [doc.id]: {...doc.data(), id: doc.id}};
                        return newData;
                    });
                });
            });
            setDataListenerUnsubscribe(() => itemUpdateSnapshot);
        };
        listenForUpdates();

        setQueryAnchor({...currentQueryAnchor, startKey: Object.keys(itemList)[0], endKey: Object.keys(itemList).slice(-1)[0]});
        setTableData(itemList);
    };

    useEffect(() => {
        if (!filters) return;
        setPage([1, 0]);
        setTableData({});
        setQueryAnchor({startKey: "", endKey: "", startQueryPos: 0, endQueryPos: 0});
        setPrevEntryIds({});
        dataListenerUnsubscribe && dataListenerUnsubscribe();
    }, [filters]);

    // Fetch new data when queries or page change
    useEffect(() => {
        console.log("SETTING TABLE DATA");
        getDataFromQuery();
        dataListenerUnsubscribe && dataListenerUnsubscribe();
    }, [page]);

    return ({...{tableData, setPage, setFilters, page}});
}


export function useDataViewerPaginator({view: initialView, sorts, queryLimit=10, additionalEntryProcessing, snapshot, filters: initialFilters, initialSort, onSearch, data}:DataViewerPaginater) {
    const [tableData, setTableData] = useState<{[key:string]:{[key:string]: unknown}}|undefined>(data ? Array.isArray(data) ? {} : Object.fromEntries(Object.entries(data).slice(0, queryLimit)) : undefined);
    const [page, setPage] = useState([1, 0]);
    const [view, setView] = useState(initialView);
    const [filters, setFilters] = useState<FilterObject|undefined>(initialFilters);
    const [queryAnchor, setQueryAnchor] = useState<{startKey: string, endKey: string, startQueryPos: number, endQueryPos: number}>({startKey: "", endKey: "", startQueryPos: 0, endQueryPos: 0});
    const [prevEntryIds, setPrevEntryIds] = useState<{[key:string]:number}>({});
    const [dataListenerUnsubscribe, setDataListenerUnsubscribe] = useState<{[key: string] : Unsubscribe}>();
    const [loading, setLoading] = useState<boolean|"loaded">(true);
    const [searchString, setSearchString] = useState<string>();
    const [sort, setSort] = useState<[string, {value: string, direction: "asc"|"desc"}]|undefined>((initialSort && sorts && sorts[initialSort]) ? [initialSort, sorts[initialSort]] : undefined);
    const [fData, setFData] = useState(data);

    const processedData = async (k: string, v: any) => additionalEntryProcessing ? await additionalEntryProcessing(k, v) : v;

    const setTableDataFromDefinedData = async () => {
        if (!fData || Array.isArray(fData)) return;

        const dataWithAdditionalProcessingPossibleNulls: [string, any][] = (await Promise.all(Object.entries(fData).map(async ([k, v]) => [k, await processedData(k, v)])));
        const dataWithAdditionalProcessing = dataWithAdditionalProcessingPossibleNulls.filter(([, v]) => v);

        const searchedData: [string, any][] = searchString ? dataWithAdditionalProcessing.filter(([, v]) => {
            const values = Object.values(v).join(", ");
            console.log("VALUESTRING", v);
            return values.includes(searchString);
        }) : dataWithAdditionalProcessing;

        const filteredData:[string, any][] = filters && Object.keys(filters).length > 0 ? searchedData.filter(([, dataValue]) => Object.entries(filters).every(([filterKey, filterValue]) => {
            const value = dataValue[filterKey];
            if ((typeof value === "number") && value === parseInt(filterValue.value as string)) return true;
            if ((typeof value === "boolean") && value === (filterValue.value === "true")) return true;
            if ((typeof value === "boolean") && value === (filterValue.value === "false")) return true;

            if ((typeof value === "string" || Array.isArray(value)) && value.includes(filterValue.value as string)) return true;
            return false;
        })) : searchedData;
        if (view === "table") {
            if (!queryLimit) throw new Error("Tables must have a limit defined.");
            const newData:[string, any][] = filteredData.slice((page[0] - 1)*queryLimit, page[0]*queryLimit);
            setTableData(Object.fromEntries(newData));
            if (Object.keys(Object.fromEntries(newData)).pop() === Object.keys(fData).pop()) {
                setLoading("loaded");
            } else {
                setLoading(false);
            }
            return;
        }
        if (view === "list") {
            setTableData(Object.fromEntries(filteredData));
            setLoading("loaded");
        }
    };

    const getDataFromQuery = async (
        itemList: {[key: string]: any} = {}, currentQueryAnchor=queryAnchor, cursorDirection?:"increase"|"decrease"|undefined, prevEntries=prevEntryIds, loadMoreFromQuery=false):Promise<any> => {
        ~
        // if (!filters) return;
        setLoading(true);
        if (!Array.isArray(fData)) {
            setTableDataFromDefinedData();
            return;
        }
        if (!queryLimit) throw new Error("Firestore queries must have a limit defined.");

        if (onSearch && (searchString || sort)) {
            if (typeof onSearch === "boolean") throw new Error("When using Firestore queries, an onSearch function should be passed to retrieve data externally. Additional processing is however completed in this hook.");
            const searchData = await onSearch(searchString, sort, page[0], filters, queryLimit);
            console.log("Search data", searchData);
            const dataWithAdditionalProcessing = ((await Promise.all(Object.entries(searchData).map(async ([k, v]) => [k, await processedData(k, v)]))).filter(([, v]) => v));
            console.log("dataWithAdditionalProcessing", dataWithAdditionalProcessing);

            setTableData((old) => {
                console.log("Setting table data from search");
                if (view === "table") {
                    console.log("Setting table data as table");

                    return {...Object.fromEntries(dataWithAdditionalProcessing)};
                }
                console.log("Setting table data as list");
                return {...old, ...Object.fromEntries(dataWithAdditionalProcessing)};
            });

            if (dataWithAdditionalProcessing.length < queryLimit) {
                setLoading("loaded");
            } else {
                setLoading(false);
            }
            return;
        }

        let cursorPos:number;
        if (page[0] > page[1]) {
            cursorPos = currentQueryAnchor.endQueryPos;
        } else {
            cursorPos = currentQueryAnchor.startQueryPos;
        }

        const querySchema:QueryObject = fData[cursorPos];

        const createQuery = (queryData:QueryObject) => {
            const constraints:any[] = [];
            queryData.where && queryData.where.forEach((w) => {
                constraints.push(where(...w));
            });

            console.log("FILTERS IN DATA PAGINATOR", filters);

            filters && Object.entries(filters).filter(([, value]) => value.value).forEach(([key, value]) => {
                if (value.type === "dropdown" && value.values && typeof value.values[value.value as string] !== "string") {
                    const chosenValue = value.values[value.value as string] as {
                        label: string;
                        test: QueryFieldFilterConstraint;
                    };
                    constraints.push(chosenValue.test);
                } else {
                    const filterValue = (value.type === "number" || value.type === "dropdown") ? parseInt(value.value as string) || value.value : value.value;
                    constraints.push(where(key, "==", filterValue));
                }
            });

            constraints.push(orderBy(queryData.orderBy ? queryData.orderBy === "documentId" ? documentId() : queryData.orderBy : documentId()));

            if (page[0] > page[1] && !cursorDirection) { // Going up
                currentQueryAnchor.endKey && constraints.push(startAfter(currentQueryAnchor.endKey));
                constraints.push(limit(queryLimit));
                if (!loadMoreFromQuery) {
                    currentQueryAnchor = {...currentQueryAnchor, startQueryPos: currentQueryAnchor.endQueryPos};
                }
            } else if (page[0] < page[1] && !cursorDirection) { // Going down
                if (!loadMoreFromQuery) {
                    currentQueryAnchor = {...currentQueryAnchor, endQueryPos: currentQueryAnchor.startQueryPos};
                }
                constraints.push(limitToLast(queryLimit));
                if (currentQueryAnchor.startKey) {
                    currentQueryAnchor.startKey && constraints.push(endBefore(currentQueryAnchor.startKey));
                } else {
                    currentQueryAnchor.startKey && constraints.push(endAt(currentQueryAnchor.startKey));
                }
            } else {
                if (cursorDirection === "decrease") {
                    constraints.push(limitToLast(queryLimit));
                } else {
                    constraints.push(limit(queryLimit));
                }
            }
            return constraints;
        };

        const constraints = createQuery(querySchema);
        const q = query(collection(db, ...(querySchema.path as [any])), ...(constraints));

        console.log("Fetching docs", constraints);

        if (snapshot) {
        // Use onSnapshot to get real-time updates
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                handleQuerySnapshot(querySnapshot);
            });
            // Save the unsubscribe function to state so we can dispose of it later
            setDataListenerUnsubscribe((d) => ({...(d || {}), [cursorPos]: unsubscribe}));
            return;
        } else {
        // Just get the docs without setting up a listener
            const queryData = await getDocs(q);
            handleQuerySnapshot(queryData);
        }

        // Function to handle query snapshot
        async function handleQuerySnapshot(querySnapshot: QuerySnapshot) {
            if (!Array.isArray(fData)) throw new Error("Called querySnapshot but data is defined.");
            if (!queryLimit) throw new Error("Firestore queries must have a limit defined.");

            const queryResults: { [key: string]: { [key: string]: unknown } } = {};
            let index = 0; // Declare the index variable

            const reverseIfBack = (docs: QueryDocumentSnapshot<DocumentData>[]) =>
                page[0] < page[1] ? docs.reverse() : docs;

            // Process each document in the querySnapshot
            for (const doc of reverseIfBack(querySnapshot.docs)) {
                if ((Object.keys(queryResults).length + Object.keys(itemList).length) === queryLimit) {
                    break;
                }

                let position = Object.keys(itemList).length+(page[0]-1)*queryLimit+index+1;
                if (page[0] < page[1]) {
                    position = (page[0])*queryLimit-index-Object.keys(itemList).length;
                }


                if (itemList[doc.id] || (prevEntries[doc.id] && prevEntries[doc.id] !== position)) {
                    console.log("Removing ", doc.id, ": E=", prevEntries[doc.id], ", G=", position);
                    continue;
                }

                let item: {[key: string]: unknown}|false = doc.data();
                item.id = doc.id;

                // Apply additionalEntryProcessing if provided
                if (additionalEntryProcessing) {
                    item = await additionalEntryProcessing(doc.id, item);
                }
                if (!item) continue;

                queryResults[doc.id] = item;
                index += 1;

                if (prevEntries[doc.id]) continue;
                prevEntries[doc.id] = position;
            }

            if (cursorDirection === "decrease" || page[0] < page[1]) {
                itemList = {...Object.fromEntries(Object.entries(queryResults).reverse()), ...itemList};
            } else {
                itemList = {...itemList, ...queryResults};
            }

            // Updating state with the new data and query anchors


            setPrevEntryIds(prevEntries);

            if (querySnapshot.size < queryLimit && Object.keys(itemList).length < queryLimit) {
            // If we have ran out of entries, increase or decrease the index.
                if (page[0] > page[1] && cursorPos+1 < fData.length) {
                    return getDataFromQuery(itemList, {...currentQueryAnchor, endQueryPos: currentQueryAnchor.endQueryPos+1}, "increase", prevEntries);
                } else if (page[0] < page[1] && cursorPos > 0) {
                    return getDataFromQuery(itemList, {...currentQueryAnchor, startQueryPos: currentQueryAnchor.startQueryPos-1}, "decrease", prevEntries);
                }
            }

            if (Object.keys(itemList).length < queryLimit && querySnapshot.size === queryLimit) {
                console.log("Shorter than ten");
                return getDataFromQuery(itemList, {...currentQueryAnchor, startKey: Object.keys(itemList)[0], endKey: Object.keys(itemList).slice(-1)[0]}, undefined, prevEntries, true);
            }

            if (querySnapshot.size === 0 &&
                Object.keys(itemList).length === 0 &&
                currentQueryAnchor.endQueryPos+1 === fData.length &&
                page[0] > 1) {
                if (view === "table") {
                    setTableData({});
                }
                setQueryAnchor((a) => ({...a, startKey: ""}));
                setLoading("loaded");
                return;
            }

            if (querySnapshot.size < queryLimit) {
                setLoading("loaded");
            } else {
                setLoading(false);
            }

            setQueryAnchor({...currentQueryAnchor, startKey: Object.keys(itemList)[0], endKey: Object.keys(itemList).slice(-1)[0]});

            setTableData((old) => {
                if (view === "table") {
                    return {...itemList};
                }
                return {...old, ...itemList};
            });
        }
    };

    const reset = () => {
        console.log("Resetting after filters?");
        setPage([1, 0]);
        setTableData({});
        setQueryAnchor({startKey: "", endKey: "", startQueryPos: 0, endQueryPos: 0});
        setPrevEntryIds({});
        dataListenerUnsubscribe && Object.values(dataListenerUnsubscribe).map((u) => u());
        setDataListenerUnsubscribe(undefined);
    };

    useEffect(() => {
        if (objectsEqualNew(fData, data)) return;
        console.log("data reset.", fData, data);
        setFData(data);
        reset();
    }, [data]);

    const updateFilters = (fFilters?: FilterObject) => {
        if (!objectsEqualNew(fFilters, filters)) {
            console.log("FILTER RESET", fFilters, filters);
            setFilters(fFilters);
            reset();
        }
    };

    const updateSearch = (search: string) => {
        if (searchString === search) return;
        console.log("SEARCH RESET", searchString, search);
        setSearchString(search);
        reset();
    };

    const updateView = (v: "list" | "table") => {
        if (v === view) return;
        console.log("View reset", view, v);
        setView(v);
        reset();
    };


    // Fetch new data when queries or page change
    useEffect(() => {
        getDataFromQuery();
        dataListenerUnsubscribe && Object.values(dataListenerUnsubscribe).map((u) => u());
    }, [page]);

    const pageUp = () => {
        setPage((p) => ([p[0]+1, p[0]]));
    };

    const pageDown = () => {
        setPage((p) => ([p[0]-1, p[0]]));
    };

    const updateSort = (sortLabel?: string) => {
        if (!sortLabel) {
            setSort(undefined);
        } else {
            if (!sorts || !sorts[sortLabel]) return;
            setSort([sortLabel, sorts[sortLabel]]);
        }
        reset();
    };

    return ({...{tableData, pageUp, pageDown, search: searchString, setFilters: updateFilters, page: page[0], sorts, loading, sort, updateSort: updateSort, setView: updateView, updateSearch}});
}

